import React, { useEffect, useState, useMemo } from "react";
import { NavLink, Outlet, useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { useTheme } from "@/context/ThemeContext";
import { useI18n } from "@/context/I18nContext";
import { Button } from "@/components/ui/button";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { Sheet, SheetContent, SheetTrigger } from "@/components/ui/sheet";
import CommandPalette from "@/components/CommandPalette";
import OAuthOnboarding from "@/components/OAuthOnboarding";
import NotificationBell from "@/components/NotificationBell";
import Footer from "@/components/Footer";
import {
  House, Users, ClockCounterClockwise, Money, ChatCircle, Megaphone,
  Calendar as CalIcon, ChartBar, Bell, Gear, SignOut, List, UserGear,
  ClipboardText, CalendarBlank, ChartLine, MagnifyingGlass, Sun, Moon, ShieldStar,
  DeviceMobile, UserSwitch,
} from "@phosphor-icons/react";

const NAV = [
  { to: "/", icon: House, key: "nav.home", roles: ["admin", "hr", "gm", "manager", "staff", "accounting"] },
  { to: "/personnel", icon: Users, key: "nav.personnel", roles: ["admin", "hr", "gm", "manager", "staff", "accounting"] },
  { to: "/pending", icon: UserGear, key: "nav.pending", roles: ["admin", "hr", "gm"] },
  { to: "/leaves", icon: ClockCounterClockwise, key: "nav.leaves", roles: ["admin", "hr", "gm", "manager", "staff", "accounting"] },
  { to: "/leaves/reconciliation", icon: ClipboardText, key: "nav.reconciliation", roles: ["admin", "hr", "gm", "manager", "staff"] },
  { to: "/shifts", icon: CalendarBlank, key: "nav.shifts", roles: ["admin", "hr", "gm", "manager", "staff"] },
  { to: "/payroll", icon: Money, key: "nav.payroll", roles: ["admin", "hr", "gm", "manager", "staff"] },
  { to: "/messages", icon: ChatCircle, key: "nav.messages", roles: ["admin", "hr", "gm", "manager", "staff", "accounting"] },
  { to: "/announcements", icon: Megaphone, key: "nav.announcements", roles: ["admin", "hr", "gm", "manager", "staff", "accounting"] },
  { to: "/calendar", icon: CalIcon, key: "nav.calendar", roles: ["admin", "hr", "gm", "manager", "staff", "accounting"] },
  { to: "/surveys", icon: ChartLine, key: "nav.surveys", roles: ["admin", "hr", "gm", "manager", "staff"] },
  { to: "/reports", icon: ChartBar, key: "nav.reports", roles: ["admin", "hr", "gm", "manager"] },
  { to: "/reports/monthly-leaves", icon: ClockCounterClockwise, key: "nav.monthly_leaves", roles: ["admin", "hr", "gm"] },
  { to: "/promotions", icon: ChartLine, key: "nav.promotions", roles: ["admin", "hr", "gm", "manager", "staff"] },
  { to: "/norm-cadre", icon: Users, key: "nav.norm_cadre", roles: ["admin", "hr", "gm", "manager"] },
  { to: "/hr-center", icon: ShieldStar, key: "nav.hr_center", roles: ["admin", "hr", "gm"] },
  { to: "/audit", icon: ClipboardText, key: "nav.audit", roles: ["admin", "hr", "gm"] },
  { to: "/delegations", icon: UserSwitch, key: "nav.delegations", roles: ["admin", "hr", "gm", "manager"] },
  { to: "/notifications", icon: Bell, key: "nav.notifications", roles: ["admin", "hr", "gm", "manager", "staff", "accounting"] },
  { to: "/payroll-admin", icon: Money, key: "nav.payroll_admin", roles: ["admin", "hr", "gm"] },
  { to: "/settings", icon: Gear, key: "nav.settings", roles: ["admin", "hr", "gm", "manager", "staff", "accounting"] },
  { to: "/guide", icon: House, key: "nav.guide", roles: ["admin", "hr", "gm", "manager", "staff", "accounting"] },
];

const ROLE_KEY = { admin: "role.admin", hr: "role.hr", gm: "role.gm", manager: "role.manager", staff: "role.staff", accounting: "role.accounting" };

function SidebarContent({ user, onNav }) {
  const { t } = useI18n();
  const items = useMemo(() => NAV.filter((n) => n.roles.includes(user?.role)), [user?.role]);
  return (
    <div className="flex flex-col h-full">
      <div className="px-5 py-6 border-b border-border">
        <div className="font-display text-2xl font-bold tracking-tight text-primary">Colossae</div>
        <div className="text-xs uppercase tracking-[0.2em] text-muted-foreground mt-1">Personnel</div>
      </div>
      <nav className="flex-1 px-3 py-4 space-y-1 overflow-y-auto">
        {items.map((n) => (
          <NavLink
            key={n.to}
            to={n.to}
            end={n.to === "/"}
            onClick={onNav}
            data-testid={`nav-${n.to.replace("/", "") || "home"}`}
            className={({ isActive }) => `nav-link ${isActive ? "active" : ""}`}
          >
            <n.icon size={18} weight="duotone" />
            <span>{t(n.key)}</span>
          </NavLink>
        ))}
      </nav>
      <div className="p-4 border-t border-border">
        <div className="flex items-center gap-3">
          <Avatar>
            <AvatarImage src={user?.photo_url ? `${process.env.REACT_APP_BACKEND_URL}${user.photo_url}` : undefined} />
            <AvatarFallback>{(user?.first_name || "?")[0]}{(user?.last_name || "")[0]}</AvatarFallback>
          </Avatar>
          <div className="min-w-0">
            <div className="text-sm font-semibold truncate">{user?.first_name} {user?.last_name}</div>
            <div className="text-xs text-muted-foreground truncate">{t(ROLE_KEY[user?.role]) || user?.role}</div>
          </div>
        </div>
      </div>
    </div>
  );
}

export default function Layout() {
  const { user, logout } = useAuth();
  const { theme, toggle } = useTheme();
  const { t, lang, setLang } = useI18n();
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const [cmd, setCmd] = useState(false);
  const [installPromptEvt, setInstallPromptEvt] = useState(null);
  const [installed, setInstalled] = useState(false);

  useEffect(() => {
    const isStandalone = window.matchMedia("(display-mode: standalone)").matches
      || window.navigator.standalone === true;
    setInstalled(isStandalone);
    const onBip = (e) => { e.preventDefault(); setInstallPromptEvt(e); };
    const onInstalled = () => { setInstalled(true); setInstallPromptEvt(null); };
    window.addEventListener("beforeinstallprompt", onBip);
    window.addEventListener("appinstalled", onInstalled);
    return () => {
      window.removeEventListener("beforeinstallprompt", onBip);
      window.removeEventListener("appinstalled", onInstalled);
    };
  }, []);

  const promptInstall = async () => {
    if (!installPromptEvt) {
      // iOS Safari: no beforeinstallprompt event — show a small hint.
      alert("Bu tarayıcıda yükleme için: Paylaş menüsü → 'Ana Ekrana Ekle' seçeneğini kullanın.");
      return;
    }
    installPromptEvt.prompt();
    await installPromptEvt.userChoice;
    setInstallPromptEvt(null);
  };

  useEffect(() => {
    const onKey = (e) => {
      if ((e.metaKey || e.ctrlKey) && e.key.toLowerCase() === "k") {
        e.preventDefault();
        setCmd((v) => !v);
      }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, []);

  return (
    <div className="min-h-screen flex">
      <aside className="hidden md:flex md:w-64 lg:w-72 flex-col bg-card border-r border-border">
        <SidebarContent user={user} />
      </aside>

      <div className="flex-1 flex flex-col min-w-0">
        <header className="bg-card border-b border-border px-4 md:px-6 h-14 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <Sheet open={open} onOpenChange={setOpen}>
              <SheetTrigger asChild>
                <Button variant="ghost" size="icon" className="md:hidden" data-testid="mobile-menu-btn">
                  <List size={22} />
                </Button>
              </SheetTrigger>
              <SheetContent side="left" className="p-0 w-72">
                <SidebarContent user={user} onNav={() => setOpen(false)} />
              </SheetContent>
            </Sheet>
            <div className="font-display font-semibold text-lg">{t("app.name")}</div>
          </div>
          <div className="flex items-center gap-2">
            <Button variant="ghost" size="sm" onClick={() => setCmd(true)} data-testid="cmdk-btn" className="gap-2">
              <MagnifyingGlass size={16} />
              <span className="hidden md:inline text-xs text-muted-foreground">{t("topbar.search")} (⌘K)</span>
            </Button>
            <div className="relative">
              <Button variant="ghost" size="sm" data-testid="lang-toggle" className="text-xs font-semibold w-10" onClick={() => {
                const codes = ["tr", "en", "id"];
                const idx = codes.indexOf(lang);
                setLang(codes[(idx + 1) % codes.length]);
              }}>
                {(lang || "tr").toUpperCase()}
              </Button>
            </div>
            {!installed && (
              <Button variant="ghost" size="sm" onClick={promptInstall} data-testid="install-app-btn" className="gap-1" title={t("topbar.install")}>
                <DeviceMobile size={16} />
                <span className="hidden lg:inline text-xs">{t("topbar.install")}</span>
              </Button>
            )}
            <Button variant="ghost" size="icon" onClick={toggle} data-testid="theme-toggle" title={t("topbar.theme")}>
              {theme === "dark" ? <Sun size={18} /> : <Moon size={18} />}
            </Button>
            <NotificationBell />
            <Button variant="outline" size="sm" onClick={logout} data-testid="logout-btn">
              <SignOut size={16} className="mr-1" /> {t("topbar.logout")}
            </Button>
          </div>
        </header>

        <main className="flex-1 p-4 md:p-6 lg:p-8 overflow-x-hidden">
          <Outlet />
        </main>
        <Footer />
      </div>

      <CommandPalette open={cmd} onOpenChange={setCmd} />
      <OAuthOnboarding />
    </div>
  );
}
